import { useI18n } from "@core/i18n/i18n";
import styled from "@emotion/styled";
import { WordingKey } from "@typings/i18n-typings";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { AlternativeErrorType } from "@ui/pages/program-builder/alternative-exercise/alternative-exercises-utils";
import { AlternativeExerciseHeaderHeight } from "@ui/pages/program-builder/alternative-exercise/components/alternative-exercise-header";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect } from "react";

interface AlternativeExerciseHeaderProps {
	errorType: AlternativeErrorType | null;
	cleanError: () => void;
}

const AlternativeErrorWordingKeys = {
	[AlternativeErrorType.ThisAlreadyExists]: "programBuilder.alternativeExercises.errors.thisAlreadyExists",
	[AlternativeErrorType.OneAlreadyExists]: "programBuilder.alternativeExercises.errors.oneAlreadyExists",
	[AlternativeErrorType.MaxExercisesReached]: "programBuilder.alternativeExercises.errors.maximumReached",
} satisfies Record<AlternativeErrorType, WordingKey>;

const ANIMATED_ERROR_STYLE = {
	hidden: {
		opacity: 0,
	},
	visible: {
		opacity: 1,
	},
	exit: {
		opacity: 0,
		transition: { type: "easeOut" },
	},
};

export const AlternativeError: React.FC<AlternativeExerciseHeaderProps> = ({ errorType, cleanError }) => {
	const { format } = useI18n();
	const { isDesktop } = useDimensions();

	useEffect(() => {
		if (isNotNil(errorType)) {
			setTimeout(() => cleanError(), 3000);
		}
	}, [cleanError, errorType, format]);

	return (
		<AnimatePresence initial={false} mode="wait">
			{isNotNil(errorType) && (
				<ErrorPanel
					isDesktop={isDesktop}
					variants={ANIMATED_ERROR_STYLE}
					initial="hidden"
					animate="visible"
					exit="exit"
				>
					<p className="p-size-s-bold" style={{ color: colors.rejection }}>
						{format(AlternativeErrorWordingKeys[errorType])}
					</p>
				</ErrorPanel>
			)}
		</AnimatePresence>
	);
};

const ErrorPanel = styled(motion.div)<{ isDesktop: boolean }>`
	position: sticky;
	top: ${({ isDesktop }) => (isDesktop ? `${AlternativeExerciseHeaderHeight}px` : "0")};
	right: 0;
	left: 0;
	z-index: 10;
	background-color: ${colors.primary10};
	padding: 12px;
`;
